import React from 'react';
import {
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  ExpenseInvoiceFa3Disposition,
  ExpenseInvoiceFa3DocumentLinkDto,
  ExpenseInvoiceFa3Family,
  ExpenseInvoiceFa3LineRole,
  ExpenseInvoiceFa3LineSource,
  ExpenseInvoiceFa3SemanticDto,
  ExpenseInvoiceFa3SummaryDto,
  ExpenseInvoiceFa3SupportStatus,
  ExpenseInvoiceItemDto,
} from '../Types/expenseInvoice.types';

const FAMILY_LABELS: Record<ExpenseInvoiceFa3Family, string> = {
  prepayment: 'Zaliczkowa',
  finalSettlement: 'Rozliczeniowa',
  standardCorrection: 'Korekta standardowa',
  prepaymentCorrection: 'Korekta zaliczki',
  settlementCorrection: 'Korekta rozliczenia',
  formalCorrection: 'Korekta formalna',
  collectiveCorrection: 'Korekta zbiorcza',
  mixedTaxation: 'Mieszana stawka',
  margin: 'Procedura marży',
  simplified: 'Uproszczona',
  intraCommunitySupply: 'WDT',
  export: 'Eksport',
  standardVat: 'VAT standardowy',
};

const DISPOSITION_LABELS: Record<ExpenseInvoiceFa3Disposition, string> = {
  autoImport: 'Auto-import',
  reviewRequired: 'Do weryfikacji',
};

const SUPPORT_STATUS_LABELS: Record<ExpenseInvoiceFa3SupportStatus, string> = {
  supported: 'Wspierane',
  unsupported: 'Niewspierane',
};

const LINE_SOURCE_LABELS: Record<ExpenseInvoiceFa3LineSource, string> = {
  faWiersz: 'FAWiersz',
  zamowienieWiersz: 'Wiersz zamówienia',
  none: 'Brak',
};

const LINE_ROLE_LABELS: Record<ExpenseInvoiceFa3LineRole, string> = {
  before: 'Stan przed',
  after: 'Stan po',
  order: 'Powiązanie zamówienia',
  current: 'Pozycja bieżąca',
  delta: 'Różnica',
  referenceOnly: 'Referencja',
};

const RELATION_TYPE_LABELS: Record<ExpenseInvoiceFa3DocumentLinkDto['relationType'], string> = {
  correctedDocument: 'Dokument korygowany',
  prepaymentDocument: 'Dokument zaliczkowy',
  settlementDocument: 'Dokument rozliczany',
};

const CORRECTION_VARIANT_LABELS: Record<NonNullable<ExpenseInvoiceFa3SemanticDto['correctionVariant']>, string> = {
  beforeAfter: 'Przed / po',
  difference: 'Różnicowa',
  formalOnly: 'Formalna',
  collectivePeriod: 'Zbiorcza okresowa',
};

const getSupportSeverity = (supportStatus: ExpenseInvoiceFa3SupportStatus) =>
  supportStatus === 'unsupported' ? 'warning' : 'success';

interface ExpenseInvoiceFa3SemanticPanelProps {
  summary?: ExpenseInvoiceFa3SummaryDto | null;
  semantic?: ExpenseInvoiceFa3SemanticDto | null;
  items?: ExpenseInvoiceItemDto[];
  variant?: 'compact' | 'details';
}

const renderField = (label: string, value: React.ReactNode) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

const renderCompactChip = (label: string, color: 'default' | 'primary' | 'warning' | 'success') => (
  <Chip label={label} size="small" color={color} variant="outlined" />
);

export const ExpenseInvoiceFa3SemanticPanel: React.FC<ExpenseInvoiceFa3SemanticPanelProps> = ({
  summary,
  semantic,
  items = [],
  variant = 'details',
}) => {
  if (!summary && !semantic) {
    return null;
  }

  const unresolvedLinks = summary?.hasUnresolvedLinks || semantic?.links.some((link) => !link.isResolved);
  const needsReview = (summary?.disposition ?? semantic?.disposition) === 'reviewRequired';
  const family = summary?.family ?? semantic?.family;
  const supportStatus = summary?.supportStatus ?? semantic?.supportStatus;
  const disposition = summary?.disposition ?? semantic?.disposition;
  const itemSemantics = items.filter((item) => item.fa3Semantic);

  if (!family || !supportStatus || !disposition) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {renderCompactChip(FAMILY_LABELS[family], 'primary')}
          {renderCompactChip(DISPOSITION_LABELS[disposition], needsReview ? 'warning' : 'success')}
          {renderCompactChip(SUPPORT_STATUS_LABELS[supportStatus], getSupportSeverity(supportStatus))}
          {unresolvedLinks ? renderCompactChip('Nierozwiązane relacje', 'warning') : null}
        </Stack>
        {summary?.unsupportedReason ? (
          <Typography variant="caption" color="text.secondary">
            {summary.unsupportedReason}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Semantyka FA(3)
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {needsReview ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Dokument ma dyspozycję <strong>{DISPOSITION_LABELS[disposition]}</strong> i wymaga
            ręcznej weryfikacji przed dalszym użyciem.
          </Alert>
        ) : null}

        {unresolvedLinks ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nierozwiązane relacje dokumentów wymagają weryfikacji.
          </Alert>
        ) : null}

        {unresolvedLinks ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Backfill FA(3) domyślnie pomija rekordy oznaczone jako ręcznie naprawione
            (`includeManualFixed=false`).
          </Alert>
        ) : null}

        {supportStatus === 'unsupported' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {semantic?.unsupportedReason ? (
              <>
                Powód braku wsparcia: <strong>{semantic.unsupportedReason}</strong>
              </>
            ) : (
              <>Backend oznaczył tę semantykę FA(3) jako niewspieraną.</>
            )}
          </Alert>
        ) : null}

        <Grid container spacing={3} sx={{ mb: semantic ? 0 : 1 }}>
          {renderField('Rodzina FA(3)', FAMILY_LABELS[family])}
          {renderField('Dyspozycja', DISPOSITION_LABELS[disposition])}
          {renderField('Status wsparcia', SUPPORT_STATUS_LABELS[supportStatus])}
          {semantic ? renderField('Źródło linii', LINE_SOURCE_LABELS[semantic.lineSource]) : null}
          {semantic ? renderField('Rodzaj źródła', semantic.sourceDocumentKind) : null}
          {semantic
            ? renderField(
                'Wariant korekty',
                semantic.correctionVariant ? CORRECTION_VARIANT_LABELS[semantic.correctionVariant] : '—'
              )
            : null}
          {semantic ? renderField('Powód korekty', semantic.correctionReason || '—') : null}
          {semantic ? renderField('Źródłowy numer KSeF', semantic.sourceKsefNumber || '—') : null}
        </Grid>

        {semantic?.links.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Relacje dokumentów
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Typ relacji</TableCell>
                    <TableCell>Referencja</TableCell>
                    <TableCell>Numer KSeF</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {semantic.links.map((link, index) => (
                    <TableRow key={`${link.relationType}-${index}`}>
                      <TableCell>{RELATION_TYPE_LABELS[link.relationType]}</TableCell>
                      <TableCell>
                        {link.relatedDocumentReference || link.externalInvoiceNumber || '—'}
                      </TableCell>
                      <TableCell>{link.externalKsefNumber || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={link.isResolved ? 'success' : 'warning'}
                          label={link.isResolved ? 'Rozwiązana' : 'Do rozwiązania'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}

        {itemSemantics.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Role linii
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pozycja</TableCell>
                    <TableCell>Rola</TableCell>
                    <TableCell>Sekcja źródła</TableCell>
                    <TableCell>Nr linii</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemSemantics.map((item, index) => (
                    <TableRow key={`${item.id}-${index}`}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{LINE_ROLE_LABELS[item.fa3Semantic!.lineRole]}</TableCell>
                      <TableCell>{item.fa3Semantic?.sourceSection || '—'}</TableCell>
                      <TableCell>{item.fa3Semantic?.sourceLineNumber ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ExpenseInvoiceFa3SemanticPanel;
